import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { loadSupabase } from "../lib/loadSupabase";
import { api } from "../lib/api";
import { authErrorMessage, normalizeAuthEmail } from "../lib/authErrors";

const AuthContext = createContext(null);

const getSupabaseStorageKey = () => {
  const url = import.meta.env.VITE_SUPABASE_URL;
  if (!url) return null;
  try {
    const { hostname } = new URL(url);
    const ref = hostname.split('.')[0];
    return ref ? `sb-${ref}-auth-token` : null;
  } catch (_) {
    return null;
  }
};

const clearLocalAuthCache = () => {
  if (typeof localStorage !== 'undefined') {
    const key = getSupabaseStorageKey();
    if (key) localStorage.removeItem(key);
  }
  if (typeof sessionStorage !== 'undefined') {
    const key = getSupabaseStorageKey();
    if (key) sessionStorage.removeItem(key);
  }
};

const decodeJwtPayload = (token) => {
  if (!token) return null;
  try {
    const [, payload] = token.split(".");
    if (!payload) return null;
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "===".slice((base64.length + 3) % 4);
    return JSON.parse(atob(padded));
  } catch (_) {
    return null;
  }
};

const deriveRoleFromSession = (session, user) => {
  const payload = decodeJwtPayload(session?.access_token);
  return payload?.role || user?.app_metadata?.role || user?.user_metadata?.role || null;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [groupMemberships, setGroupMemberships] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchGroupMemberships = async (uid) => {
    if (!uid) {
      setGroupMemberships([]);
      return [];
    }
    try {
      const res = await api.getMyGroupMemberships();
      const memberships = res?.memberships || [];
      setGroupMemberships(memberships);
      return memberships;
    } catch (e) {
      setGroupMemberships([]);
      return [];
    }
  };

  useEffect(() => {
    let isMounted = true;
    let subscription;
    (async () => {
      setLoading(true);
      try {
        const supabase = await loadSupabase();
        if (!isMounted) return;
        ({ data: { subscription } } = supabase.auth.onAuthStateChange(async (_evt, sess) => {
          if (!isMounted) return;

          const u = sess?.user || null;
          setUser(u);
          if (u) {
            const sessionRole = deriveRoleFromSession(sess, u);
            setRole(sessionRole);
            await fetchGroupMemberships(u.id);
          } else {
            setRole(null);
            setGroupMemberships([]);
          }
        }));
        // Lägg till timeout för getSession för att undvika att hänga
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Session timeout')), 5000)
        );
        
        const { data, error } = await Promise.race([sessionPromise, timeoutPromise]).catch(() => ({ data: null, error: null }));
        
        if (!isMounted) return;
        
        if (error) {
          // ignore
        } else if (data) {
          const session = data.session || null;
          const currUser = session?.user || null;
          setUser(currUser);
          const sessionRole = deriveRoleFromSession(session, currUser);
          setRole(sessionRole);
          if (currUser) {
            await fetchGroupMemberships(currUser.id);
          }
        }
      } catch (e) {
        if (!isMounted) return;
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  const signInWithPassword = async (email, password) => {
    setError(null);
    setLoading(true);
    try {
      const supabase = await loadSupabase();
      const { data, error } = await supabase.auth.signInWithPassword({ email: normalizeAuthEmail(email), password });
      if (error) {
        setError(authErrorMessage(error));
        return { data, error };
      }
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      const confirmedSession = sessionData?.session || null;

      if (sessionError || !confirmedSession) {
        const message = "Login lyckades mot auth men ingen browser-session etablerades.";
        setError(message);
        return {
          data: null,
          error: { message },
        };
      }

      return {
        data: { ...data, session: confirmedSession },
        error: null,
      };
    } catch (e) {
      const errMsg = authErrorMessage(e);
      setError(errMsg);
      return { data: null, error: { message: errMsg } };
    } finally {
      setLoading(false);
    }
  };

  const signInWithMagicLink = async (email, { redirectTo } = {}) => {
    setError(null);
    try {
      const supabase = await loadSupabase();
      const { data, error } = await supabase.auth.signInWithOtp({
        email: normalizeAuthEmail(email),
        options: {
          emailRedirectTo: redirectTo || window.location.origin + "/login",
          shouldCreateUser: false,
        },
      });
      if (error) setError(authErrorMessage(error));
      return { data, error };
    } catch (error) {
      setError(authErrorMessage(error));
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      const supabase = await loadSupabase();
      await supabase.auth.signOut();
    } catch (_) {
      // non-blocking
    } finally {
      clearLocalAuthCache();
      setUser(null);
      setRole(null);
      setGroupMemberships([]);
      setError(null);
    }
  };

  const refreshGroupMemberships = async (uid = user?.id) => fetchGroupMemberships(uid);

  const value = useMemo(
    () => ({
      user,
      role,
      groupMemberships,
      loading,
      error,
      signInWithPassword,
      signInWithMagicLink,
      signOut,
      refreshGroupMemberships,
    }),
    [user, role, groupMemberships, loading, error]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
